// «Promo del equipo» en el detalle de «Elige el teléfono»: radio para las que van solas (con
// «Sin promo») y casilla para las que se suman. Solo marca; se aparta al continuar.
import { Ban, Check, Gift, Sparkles, Ticket, Wallet, type LucideIcon } from 'lucide-react';
import { Button } from '@/kit';
import { cn } from '@/lib/cn';
import { PROMOTION_GROUP_TONE } from '@/domains/sales/wizard/promotionConstants';
import type { SalePromotionTone } from '@/domains/sales/wizard/promotionSlots';
import type { CatalogItem } from '@/domains/sales/wizard/types';
import { formatMXN } from '@/shared/lib/format/money';
import { updatePhoneDraft, usePhoneDraft, usePhonePromotionOptions } from '../hooks/usePhoneDraft';
import { withDraftSolo, withDraftStackable, type DraftOption } from '../lib/phoneDraft';

const TONE_ICON: Record<SalePromotionTone, LucideIcon> = {
    discount: Sparkles,
    gift: Gift,
    benefit: Wallet,
    coupon: Ticket,
};

export interface TeamPromotionSelectorProps {
    sessionId: string;
    item: CatalogItem;
    /** Línea del teléfono si ya está apartado en la venta. */
    heldLineId?: string;
    /** SureKeep no financia el equipo o el cliente no califica: sin las solo SureKeep. */
    withoutSureKeep?: boolean;
    /** Kit apartado o venta cobrada: se ve, no se cambia. */
    readOnly?: boolean;
}

export function TeamPromotionSelector({
    sessionId,
    item,
    heldLineId,
    withoutSureKeep = false,
    readOnly = false,
}: TeamPromotionSelectorProps) {
    const promotions = usePhonePromotionOptions(sessionId, item, heldLineId, withoutSureKeep);
    const draft = usePhoneDraft(sessionId, item.sku, heldLineId, promotions);
    const { options } = promotions;

    if (promotions.isError) {
        return (
            <section
                aria-label="Promo del equipo"
                role="alert"
                className="mt-3 flex flex-col gap-1.5 rounded-xl border border-border p-3 text-[12px]"
            >
                <span className="text-destructive">No pudimos cargar las promos del equipo.</span>
                <Button
                    size="sm"
                    variant="secondary"
                    className="self-start"
                    onClick={promotions.retry}
                >
                    Reintentar
                </Button>
            </section>
        );
    }
    if (!promotions.ready || options.length === 0) return null;

    const solos = options.filter((o) => !o.stackable);
    const stackables = options.filter((o) => o.stackable);
    const update = (change: Parameters<typeof updatePhoneDraft>[2]): void => {
        updatePhoneDraft(sessionId, item.sku, change);
    };
    const tone = PROMOTION_GROUP_TONE.discount;

    return (
        <section
            aria-label="Promo del equipo"
            className={cn(
                'mt-3 flex flex-col gap-1 rounded-xl border border-success/30 p-3',
                tone.box,
            )}
        >
            <div
                className={cn(
                    'mb-1 flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.08em]',
                    tone.text,
                )}
            >
                <Sparkles className="size-3.5" aria-hidden />
                Promo del equipo
            </div>
            {solos.length > 0 && (
                <div role="radiogroup" aria-label="Van solas" className="flex flex-col">
                    {solos.map((option) => (
                        <OptionRow
                            key={option.promotionId}
                            option={option}
                            role="radio"
                            checked={draft?.soloId === option.promotionId}
                            disabled={readOnly}
                            onToggle={() => {
                                update((d) => withDraftSolo(d, option.promotionId));
                            }}
                        />
                    ))}
                    <MarkerRow
                        role="radio"
                        checked={draft?.declined === true}
                        disabled={readOnly}
                        icon={Ban}
                        label="Sin promo"
                        onToggle={() => {
                            update((d) => withDraftSolo(d, null));
                        }}
                    />
                </div>
            )}
            {stackables.length > 0 && (
                <div role="group" aria-label="Se suman" className="flex flex-col">
                    {stackables.map((option) => {
                        const checked = draft?.stackableIds.includes(option.promotionId) ?? false;
                        return (
                            <OptionRow
                                key={option.promotionId}
                                option={option}
                                role="checkbox"
                                checked={checked}
                                disabled={readOnly}
                                onToggle={() => {
                                    update((d) =>
                                        withDraftStackable(d, option.promotionId, !checked),
                                    );
                                }}
                            />
                        );
                    })}
                </div>
            )}
        </section>
    );
}

function OptionRow({
    option,
    role,
    checked,
    disabled,
    onToggle,
}: {
    option: DraftOption;
    role: 'radio' | 'checkbox';
    checked: boolean;
    disabled: boolean;
    onToggle: () => void;
}) {
    // Un regalo de varias opciones se marca aquí y se elige en «Arma la venta».
    const note =
        option.unavailable ??
        (option.giftPending ? 'El regalo se elige en el siguiente paso' : null);
    return (
        <MarkerRow
            role={role}
            checked={checked && option.unavailable === null}
            disabled={disabled || option.unavailable !== null}
            icon={TONE_ICON[option.tone]}
            label={option.name}
            note={note}
            sureKeepOnly={option.sureKeepOnly}
            amountCents={option.discountCents}
            onToggle={onToggle}
        />
    );
}

/** Renglón compacto: marcador, ícono, nombre y lo que baja. */
function MarkerRow({
    role,
    checked,
    disabled,
    icon: Icon,
    label,
    note = null,
    sureKeepOnly = false,
    amountCents = 0,
    onToggle,
}: {
    role: 'radio' | 'checkbox';
    checked: boolean;
    disabled: boolean;
    icon: LucideIcon;
    label: string;
    note?: string | null;
    sureKeepOnly?: boolean;
    amountCents?: number;
    onToggle: () => void;
}) {
    return (
        <button
            type="button"
            role={role}
            aria-checked={checked}
            disabled={disabled}
            onClick={onToggle}
            className={cn(
                'flex w-full items-start gap-2 rounded-md py-1 text-left text-[12.5px] transition-colors',
                checked ? 'font-medium text-foreground' : 'text-muted-foreground',
                disabled ? 'cursor-not-allowed' : 'hover:text-foreground',
                disabled && !checked && 'opacity-60',
            )}
        >
            <span
                aria-hidden
                className={cn(
                    'mt-0.5 flex size-3.5 flex-none items-center justify-center border',
                    role === 'radio' ? 'rounded-full' : 'rounded-[3px]',
                    checked
                        ? 'border-success bg-success text-success-foreground'
                        : 'border-border bg-card',
                )}
            >
                {checked && role === 'checkbox' && <Check className="size-3" />}
                {checked && role === 'radio' && <span className="size-1.5 rounded-full bg-card" />}
            </span>
            <Icon className="mt-0.5 size-3.5 flex-none" aria-hidden />
            <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate" title={label}>
                    {label}
                </span>
                {(note || sureKeepOnly) && (
                    <span className="text-[11px] font-normal text-muted-foreground">
                        {[note, sureKeepOnly ? 'Solo con crédito SureKeep' : null]
                            .filter(Boolean)
                            .join(' · ')}
                    </span>
                )}
            </span>
            {amountCents > 0 && (
                <span className="flex-none tabular-nums text-success">
                    −{formatMXN(amountCents / 100)}
                </span>
            )}
        </button>
    );
}
